import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { User } from '@supabase/supabase-js';
import type { Database } from '@/lib/database.types';

type ProfileRow = Database['public']['Tables']['profiles']['Row'];

interface AuthState {
  user: User | null;
  profile: ProfileRow | null;
  loading: boolean;
  isAdmin: boolean;
}

let globalState: AuthState = { user: null, profile: null, loading: true, isAdmin: false };
let listeners: Set<(s: AuthState) => void> = new Set();
let initialized = false;

async function fetchProfile(user: User): Promise<ProfileRow | null> {
  // Use edge function to bypass RLS issues
  const { data, error } = await supabase.functions.invoke('get-profile');
  
  if (error || !data?.profile) {
    console.error('fetchProfile error:', error || data?.error);
    return null;
  }
  
  return data.profile as ProfileRow;
}

async function updateSession() {
  const { data: { session } } = await supabase.auth.getSession();
  const user = session?.user ?? null;
  
  if (user) {
    const profile = await fetchProfile(user);
    globalState = {
      user,
      profile,
      loading: false,
      isAdmin: profile?.role === 'ADMIN',
    };
  } else {
    globalState = {
      user: null,
      profile: null,
      loading: false,
      isAdmin: false,
    };
  }

  for (const listener of listeners) {
    listener({ ...globalState });
  }
}

if (!initialized) {
  initialized = true;
  updateSession();
  supabase.auth.onAuthStateChange((_event, session) => { 
    updateSession(); 
  });
}

export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>(globalState);

  useEffect(() => {
    const listener = (s: AuthState) => setState({ ...s });
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, []);

  return state;
}

export function useRequireAuth() {
  const { user, loading } = useAuth();
  if (!loading && !user) window.location.href = '/anmelden';
  return { user, loading };
}

export function useRequireAdmin() {
  const { user, profile, loading } = useAuth();
  if (!loading) {
    if (!user) window.location.href = '/anmelden';
    else if (profile?.role !== 'ADMIN') window.location.href = '/dashboard';
  }
  return { user, profile, loading };
}

export const authUtils = {
  async login(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (data.user) {
      await supabase.from('profiles').update({ last_login_at: new Date().toISOString() }).eq('id', data.user.id).single();
    }
    return data;
  },
  async logout() {
    await supabase.auth.signOut();
    window.location.href = '/';
  },
  async resetPassword(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: '${window.location.origin}/passwort-zuruecksetzen',
    });
    if (error) throw error;
  },
  async updatePassword(newPassword: string) {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  },
};